import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Shield, Eye, Lock, Database, Cookie, Users, Bell, FileText } from "lucide-react";
import eventBus from "../../../utils/eventBus";

const PrivacyPolicyPage: React.FC = () => {
  const navigate = useNavigate();
  const lastUpdated = "December 2024";
  const effectiveDate = "December 2024";

  useEffect(() => {
    eventBus.emit("fullscreen:enter");
    return () => {
      eventBus.emit("fullscreen:exit");
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-slate-700">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-slate-800 rounded-xl transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-xl font-bold text-white">Privacy Policy</h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8 pb-24">
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl border border-slate-700 p-6 md:p-10 space-y-8">
          
          {/* Introduction Banner */}
          <div className="bg-emerald-500/20 border border-emerald-500/50 rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <Lock className="w-8 h-8 text-emerald-400 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-lg font-bold text-emerald-400 mb-2">Your Privacy Matters</h2>
                <p className="text-emerald-200 text-sm leading-relaxed">
                  This Privacy Policy explains how Arthereum Wallet collects, uses, discloses, and safeguards 
                  your information when you use our application. Please read this policy carefully to understand 
                  our practices regarding your personal data.
                </p>
              </div>
            </div>
          </div>

          {/* Meta Info */}
          <div className="text-slate-400 text-sm space-y-1">
            <p><strong>Last Updated:</strong> {lastUpdated}</p>
            <p><strong>Effective Date:</strong> {effectiveDate}</p>
          </div>

          {/* Section 1 */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center text-emerald-400 text-sm font-bold">1</span>
              <Database className="w-5 h-5 text-emerald-400" />
              Information We Collect
            </h2>
            <div className="text-slate-300 leading-relaxed space-y-4 pl-11">
              <p>We collect various types of information to provide and improve our Service:</p>
              
              <div className="space-y-4">
                <div className="bg-slate-700/30 rounded-xl p-4">
                  <h4 className="font-bold text-emerald-300 mb-2">1.1 Information from Pi Network Authentication</h4>
                  <p className="text-sm mb-2">When you authenticate through Pi Network, we receive:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm pl-4">
                    <li>Your Pi Network username</li>
                    <li>Your Pi Network user identifier (UID)</li>
                    <li>Authentication tokens for session management</li>
                    <li>Access permissions granted during authentication</li>
                  </ul>
                </div>
                
                <div className="bg-slate-700/30 rounded-xl p-4">
                  <h4 className="font-bold text-emerald-300 mb-2">1.2 Account Information</h4>
                  <p className="text-sm mb-2">Data associated with your Arthereum account:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm pl-4">
                    <li>Username and display name</li>
                    <li>Profile image (if provided)</li>
                    <li>Email address (if provided for verification)</li>
                    <li>Account creation date</li>
                    <li>Referral codes and referral relationships</li>
                  </ul>
                </div>
                
                <div className="bg-slate-700/30 rounded-xl p-4">
                  <h4 className="font-bold text-emerald-300 mb-2">1.3 Transaction and Activity Data</h4>
                  <p className="text-sm mb-2">Records of your activities within the app:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm pl-4">
                    <li>Token balances (ATH, Pi, and other cryptocurrencies)</li>
                    <li>Mining/grant claim history and timestamps</li>
                    <li>Staking transactions and stake periods</li>
                    <li>Crypto transfer records (send/receive)</li>
                    <li>Contest participation and points</li>
                    <li>Lucky spin results</li>
                    <li>Subscription and payment history</li>
                    <li>Login streaks and session data</li>
                  </ul>
                </div>
                
                <div className="bg-slate-700/30 rounded-xl p-4">
                  <h4 className="font-bold text-emerald-300 mb-2">1.4 Device and Technical Information</h4>
                  <p className="text-sm mb-2">Technical data collected automatically:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm pl-4">
                    <li>Device type and operating system</li>
                    <li>Browser type and version</li>
                    <li>IP address</li>
                    <li>Language preferences</li>
                    <li>Time zone settings</li>
                    <li>Access timestamps and session duration</li>
                    <li>Features used and interactions</li>
                  </ul>
                </div>
                
                <div className="bg-slate-700/30 rounded-xl p-4">
                  <h4 className="font-bold text-emerald-300 mb-2">1.5 Communications</h4>
                  <p className="text-sm">
                    If you contact us, we may collect information you provide in your communications, 
                    including your message content, contact information, and any attachments.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center text-emerald-400 text-sm font-bold">2</span>
              <Eye className="w-5 h-5 text-emerald-400" />
              How We Use Your Information
            </h2>
            <div className="text-slate-300 leading-relaxed space-y-3 pl-11">
              <p>We use the collected information for the following purposes:</p>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <strong>Service Provision:</strong> To operate, maintain, and provide the features and 
                    functionality of Arthereum Wallet, including processing transactions, calculating grants, 
                    and managing your account.
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <strong>Authentication:</strong> To verify your identity, authenticate your sessions, 
                    and secure your account access through Pi Network integration.
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <strong>Personalization:</strong> To customize your experience, including language 
                    preferences, theme settings, and personalized content delivery.
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <strong>Analytics and Improvement:</strong> To analyze usage patterns, identify trends, 
                    monitor app performance, and improve our Service features.
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <strong>Security:</strong> To detect, prevent, and address fraud, abuse, security issues, 
                    and technical problems.
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <strong>Communications:</strong> To send you notifications, updates, promotional materials 
                    (with your consent), and respond to your inquiries.
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <strong>Legal Compliance:</strong> To comply with legal obligations, resolve disputes, 
                    and enforce our agreements.
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <strong>Advertising:</strong> To display relevant advertisements through third-party 
                    ad networks (for non-Pro users).
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center text-emerald-400 text-sm font-bold">3</span>
              <Users className="w-5 h-5 text-emerald-400" />
              Information Sharing and Disclosure
            </h2>
            <div className="text-slate-300 leading-relaxed space-y-4 pl-11">
              <p>We may share your information in the following circumstances:</p>
              
              <div className="space-y-4">
                <div className="bg-slate-700/30 rounded-xl p-4">
                  <h4 className="font-bold text-emerald-300 mb-2">3.1 Pi Network</h4>
                  <p className="text-sm">
                    We share necessary information with Pi Network for authentication, payment processing, 
                    and integration purposes. This includes user identifiers and transaction data related to 
                    Pi deposits, withdrawals, and staking.
                  </p>
                </div>
                
                <div className="bg-slate-700/30 rounded-xl p-4">
                  <h4 className="font-bold text-emerald-300 mb-2">3.2 Advertising Partners</h4>
                  <p className="text-sm">
                    For non-Pro users, we work with advertising partners (including Pi Network Ads) to display 
                    advertisements. These partners may collect device identifiers and usage data for ad 
                    targeting and measurement purposes.
                  </p>
                </div>
                
                <div className="bg-slate-700/30 rounded-xl p-4">
                  <h4 className="font-bold text-emerald-300 mb-2">3.3 Service Providers</h4>
                  <p className="text-sm">
                    We engage third-party companies and individuals to perform services on our behalf, 
                    such as hosting, analytics, and customer support. These providers have access to your 
                    information only to perform these tasks and are obligated to protect it.
                  </p>
                </div>
                
                <div className="bg-slate-700/30 rounded-xl p-4">
                  <h4 className="font-bold text-emerald-300 mb-2">3.4 Legal Requirements</h4>
                  <p className="text-sm">
                    We may disclose your information if required to do so by law or in response to valid 
                    requests by public authorities (e.g., court orders, government requests).
                  </p>
                </div>
                
                <div className="bg-slate-700/30 rounded-xl p-4">
                  <h4 className="font-bold text-emerald-300 mb-2">3.5 Business Transfers</h4>
                  <p className="text-sm">
                    If we are involved in a merger, acquisition, or sale of assets, your information may be 
                    transferred as part of that transaction. We will notify you of any such change.
                  </p>
                </div>
                
                <div className="bg-slate-700/30 rounded-xl p-4">
                  <h4 className="font-bold text-emerald-300 mb-2">3.6 With Your Consent</h4>
                  <p className="text-sm">
                    We may share your information for any other purpose with your explicit consent.
                  </p>
                </div>
                
                <div className="bg-slate-700/30 rounded-xl p-4">
                  <h4 className="font-bold text-emerald-300 mb-2">3.7 Public Information</h4>
                  <p className="text-sm">
                    Certain information may be publicly visible, including your username, profile image, 
                    and leaderboard rankings. Contest winners may be publicly announced with their username.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center text-emerald-400 text-sm font-bold">4</span>
              <Lock className="w-5 h-5 text-emerald-400" />
              Data Security
            </h2>
            <div className="text-slate-300 leading-relaxed space-y-3 pl-11">
              <p>
                We implement appropriate technical and organizational security measures to protect your 
                personal information against unauthorized access, alteration, disclosure, or destruction.
              </p>
              <p>Our security measures include:</p>
              <ul className="list-disc list-inside space-y-2 pl-4">
                <li>Encryption of data in transit using HTTPS/TLS</li>
                <li>Secure storage with Firebase/Google Cloud infrastructure</li>
                <li>Access controls and authentication requirements</li>
                <li>Regular security assessments and monitoring</li>
                <li>Employee training on data protection practices</li>
              </ul>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mt-4">
                <p className="text-yellow-200 text-sm">
                  <strong>Important:</strong> While we strive to protect your information, no method of 
                  electronic transmission or storage is 100% secure. We cannot guarantee absolute security, 
                  and you acknowledge that you transmit your data at your own risk.
                </p>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center text-emerald-400 text-sm font-bold">5</span>
              Data Retention
            </h2>
            <div className="text-slate-300 leading-relaxed space-y-3 pl-11">
              <p>
                We retain your personal information for as long as necessary to fulfill the purposes 
                outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.
              </p>
              <p>Specifically:</p>
              <ul className="list-disc list-inside space-y-2 pl-4">
                <li><strong>Account data:</strong> Retained while your account is active</li>
                <li><strong>Transaction records:</strong> Retained for legal and audit purposes (typically 7 years)</li>
                <li><strong>Analytics data:</strong> Aggregated and anonymized after 2 years</li>
                <li><strong>Communications:</strong> Retained as needed for support and legal purposes</li>
              </ul>
              <p>
                Upon account deletion request, we will delete or anonymize your personal information within 
                30 days, except where retention is required by law or for legitimate business purposes.
              </p>
            </div>
          </section>

          {/* Section 6 */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center text-emerald-400 text-sm font-bold">6</span>
              <Cookie className="w-5 h-5 text-emerald-400" />
              Cookies and Tracking Technologies
            </h2>
            <div className="text-slate-300 leading-relaxed space-y-3 pl-11">
              <p>
                We use cookies, local storage, and similar tracking technologies to enhance your experience 
                and collect information about usage patterns.
              </p>
              <div className="space-y-3">
                <div className="bg-slate-700/30 rounded-xl p-4">
                  <h4 className="font-bold text-emerald-300 mb-2">Essential Cookies</h4>
                  <p className="text-sm">Required for basic functionality, authentication, and session management. 
                  These cannot be disabled.</p>
                </div>
                <div className="bg-slate-700/30 rounded-xl p-4">
                  <h4 className="font-bold text-emerald-300 mb-2">Preference Cookies</h4>
                  <p className="text-sm">Remember your settings such as language and theme preferences.</p>
                </div>
                <div className="bg-slate-700/30 rounded-xl p-4">
                  <h4 className="font-bold text-emerald-300 mb-2">Analytics Cookies</h4>
                  <p className="text-sm">Help us understand how users interact with the app to improve features.</p>
                </div>
                <div className="bg-slate-700/30 rounded-xl p-4">
                  <h4 className="font-bold text-emerald-300 mb-2">Advertising Cookies</h4>
                  <p className="text-sm">Used by our advertising partners to deliver relevant ads (non-Pro users only).</p>
                </div>
              </div>
              <p className="text-sm text-slate-400">
                You can manage cookie preferences through your browser settings. Note that disabling certain 
                cookies may affect app functionality.
              </p>
            </div>
          </section>

          {/* Section 7 */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center text-emerald-400 text-sm font-bold">7</span>
              Your Rights and Choices
            </h2>
            <div className="text-slate-300 leading-relaxed space-y-3 pl-11">
              <p>Depending on your jurisdiction, you may have the following rights regarding your personal data:</p>
              <ul className="list-disc list-inside space-y-2 pl-4">
                <li><strong>Access:</strong> Request a copy of your personal information</li>
                <li><strong>Correction:</strong> Request correction of inaccurate data</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                <li><strong>Portability:</strong> Receive your data in a portable format</li>
                <li><strong>Restriction:</strong> Request limitation of processing</li>
                <li><strong>Objection:</strong> Object to certain processing activities</li>
                <li><strong>Withdraw Consent:</strong> Withdraw previously given consent</li>
              </ul>
              <p>
                To exercise these rights, please contact us through the app. We will respond to your request 
                within 30 days. Note that some data may need to be retained for legal or operational purposes.
              </p>
              <div className="bg-slate-700/30 rounded-xl p-4 mt-4">
                <h4 className="font-bold text-emerald-300 mb-2">Account Deletion</h4>
                <p className="text-sm">
                  You may request account deletion at any time. Upon deletion, you will lose access to your 
                  account and any associated tokens, balances, or rewards. This action is irreversible.
                </p>
              </div>
            </div>
          </section>

          {/* Section 8 */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center text-emerald-400 text-sm font-bold">8</span>
              International Data Transfers
            </h2>
            <div className="text-slate-300 leading-relaxed space-y-3 pl-11">
              <p>
                Your information may be transferred to and processed in countries other than your country of 
                residence. These countries may have different data protection laws.
              </p>
              <p>
                By using Arthereum Wallet, you consent to the transfer of your information to other countries, 
                including the United States, where our service providers may be located.
              </p>
              <p>
                We take steps to ensure that your data receives an adequate level of protection in the 
                jurisdictions in which we process it.
              </p>
            </div>
          </section>

          {/* Section 9 */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center text-emerald-400 text-sm font-bold">9</span>
              Children's Privacy
            </h2>
            <div className="text-slate-300 leading-relaxed space-y-3 pl-11">
              <p>
                Arthereum Wallet is not intended for use by individuals under the age of 18 (or the age of 
                majority in your jurisdiction).
              </p>
              <p>
                We do not knowingly collect personal information from children. If we become aware that we 
                have collected personal information from a child without parental consent, we will take steps 
                to delete that information.
              </p>
              <p>
                If you believe that we have collected information from a child, please contact us immediately.
              </p>
            </div>
          </section>

          {/* Section 10 */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center text-emerald-400 text-sm font-bold">10</span>
              Third-Party Links and Services
            </h2>
            <div className="text-slate-300 leading-relaxed space-y-3 pl-11">
              <p>
                Our Service may contain links to third-party websites, applications, or services that are 
                not operated by us. We have no control over and assume no responsibility for the content, 
                privacy policies, or practices of any third-party sites or services.
              </p>
              <p>
                We encourage you to review the privacy policies of any third-party services before providing 
                them with your information.
              </p>
            </div>
          </section>

          {/* Section 11 */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center text-emerald-400 text-sm font-bold">11</span>
              <Bell className="w-5 h-5 text-emerald-400" />
              Changes to This Policy
            </h2>
            <div className="text-slate-300 leading-relaxed space-y-3 pl-11">
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any significant 
                changes by posting the new policy on this page and updating the "Last Updated" date.
              </p>
              <p>
                Your continued use of the Service after any changes to this Privacy Policy constitutes your 
                acceptance of the updated policy.
              </p>
              <p>
                We encourage you to review this Privacy Policy periodically for any changes.
              </p>
            </div>
          </section>

          {/* Section 12 */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center text-emerald-400 text-sm font-bold">12</span>
              California Privacy Rights (CCPA)
            </h2>
            <div className="text-slate-300 leading-relaxed space-y-3 pl-11">
              <p>
                If you are a California resident, you have specific rights under the California Consumer 
                Privacy Act (CCPA):
              </p>
              <ul className="list-disc list-inside space-y-2 pl-4">
                <li>Right to know what personal information we collect and how we use it</li>
                <li>Right to request deletion of your personal information</li>
                <li>Right to opt-out of the sale of personal information (we do not sell personal information)</li>
                <li>Right to non-discrimination for exercising your privacy rights</li>
              </ul>
              <p>
                To exercise your CCPA rights, please contact us through the app or official communication channels.
              </p>
            </div>
          </section>

          {/* Section 13 */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center text-emerald-400 text-sm font-bold">13</span>
              European Privacy Rights (GDPR)
            </h2>
            <div className="text-slate-300 leading-relaxed space-y-3 pl-11">
              <p>
                If you are located in the European Economic Area (EEA), you have rights under the General 
                Data Protection Regulation (GDPR):
              </p>
              <ul className="list-disc list-inside space-y-2 pl-4">
                <li>Right of access to your personal data</li>
                <li>Right to rectification of inaccurate data</li>
                <li>Right to erasure ("right to be forgotten")</li>
                <li>Right to restriction of processing</li>
                <li>Right to data portability</li>
                <li>Right to object to processing</li>
                <li>Right to lodge a complaint with a supervisory authority</li>
              </ul>
              <p>
                Our legal basis for processing your personal data includes: consent, performance of a contract, 
                legitimate interests, and legal obligations.
              </p>
            </div>
          </section>

          {/* Section 14 */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center text-emerald-400 text-sm font-bold">14</span>
              Contact Us
            </h2>
            <div className="text-slate-300 leading-relaxed space-y-3 pl-11">
              <p>
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data 
                practices, please contact us through the official channels provided within the Arthereum 
                Wallet application.
              </p>
              <p>
                We will endeavor to respond to all legitimate inquiries within a reasonable timeframe.
              </p>
            </div>
          </section>

          {/* Final Acknowledgment */}
          <div className="bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <Shield className="w-8 h-8 text-emerald-400 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-bold text-emerald-300 mb-2">Acknowledgment</h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  BY USING ARTHEREUM WALLET, YOU ACKNOWLEDGE THAT YOU HAVE READ AND UNDERSTOOD THIS PRIVACY 
                  POLICY AND AGREE TO THE COLLECTION, USE, AND DISCLOSURE OF YOUR PERSONAL INFORMATION AS 
                  DESCRIBED HEREIN.
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
